import { useRelayQuery, useRouterState } from '@/Utility/hooks';
import { CsvExportQuery, type Export_Csv_Query, type Export_Csv_Query$variables } from '@/graphql/queries/Export.query';
import { CsvExportQueryTypeEnum, type DateQueryInput } from '@/graphql/types';
import { Button } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const CsvExport = () => {
  const [csvDataReady, setCsvDataReady] = useState(false);
  const pathname = useRouterState().location.pathname;

  const filters = useSearch({
    strict: false,
    select: (state) => {
      return {
        visibility: state.visibility,
        author: state.author,
        hubFilter: state.hubFilter,
        search: state.search,
        category: state.category,
        date: state.date,
        mediaName: state.mediaName,
        hubTemplate: state.hubTemplate,
        hubGroup: state.hubGroup,
      };
    },
  });

  const { data, refetch, loading, reset } = useRelayQuery<Export_Csv_Query>(
    CsvExportQuery,
    undefined as unknown as Export_Csv_Query$variables,
    {
      refetchOnMount: false,
      cacheConfig: {
        fetchPolicy: 'store-or-network',
      },
    }
  );

  const exportProps = useMemo(() => {
    const validPaths = ['/dash/catalog', '/dash/znglists', '/dash/hubs/hubs', '/dash/hubs/pages']; // add hubs path
    const hasExport = validPaths.includes(pathname);
    let queryType = null;
    if (pathname === '/dash/catalog') {
      queryType = CsvExportQueryTypeEnum.Catalog;
    } else if (pathname === '/dash/znglists') {
      queryType = CsvExportQueryTypeEnum.Znglist;
    } else if (pathname === '/dash/hubs/hubs') {
      queryType = CsvExportQueryTypeEnum.Hub;
    } else if (pathname === '/dash/hubs/pages') {
      queryType = CsvExportQueryTypeEnum.HubPage;
    }
    return {
      hasExport: hasExport && queryType !== null,
      queryType,
    };
  }, [pathname]);

  const onCsvExportClick = () => {
    if (!exportProps?.hasExport) return;
    if (loading) return;

    if (!csvDataReady) {
      refetch(
        {
          input: {
            queryType: exportProps.queryType,
            statusIn: filters?.visibility?.length ? filters.visibility : null,
            authorIn: filters?.author?.length ? filters.author : null,
            search: (filters?.search as string) ?? null,
            categoryIn: (filters?.category as string[]) ?? null,
            ...(filters?.date ? { date: filters.date as DateQueryInput } : {}),
            ...((filters?.mediaName as string[])?.length
              ? {
                  metaQuery: {
                    metaArray: [
                      {
                        key: 'content_media_name',
                        value: filters.mediaName as string[],
                        compare: 'IN',
                      },
                    ],
                  },
                }
              : {}),
            ...(filters?.hubTemplate ? { hubTemplate: filters.hubTemplate } : {}),
            ...(filters?.hubGroup ? { hubGroup: filters.hubGroup } : {}),
            ...(filters?.hubFilter?.hubIds ? { hubFilter: filters.hubFilter.hubIds } : {}),
          } as Export_Csv_Query$variables['input'],
        },
        (_, errors) => {
          if (!!errors?.length) {
            toast.error('Error generating CSV. Try again with different filters');
          }
          setCsvDataReady(true);
        }
      );
      return;
    }

    if (!data?.csvExport?.csv) return;

    // csv data is ready
    const csv = data?.csvExport?.csv;

    const csvBlob = new Blob([csv], { type: 'text/csv' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const hiddenElement = document.createElement('a');
    hiddenElement.href = csvUrl;
    if (pathname.includes('dash/catalog')) {
      hiddenElement.download = `zngly-catalog-export-${new Date().toISOString()}.csv`;
    }
    if (pathname.includes('dash/znglists')) {
      hiddenElement.download = `zngly-znglists-export-${new Date().toISOString()}.csv`;
    }
    if (pathname.includes('dash/hubs/hubs')) {
      hiddenElement.download = `zngly-hubs-export-${new Date().toISOString()}.csv`;
    }
    if (pathname.includes('dash/hubs/pages')) {
      hiddenElement.download = `zngly-hub-pages-export-${new Date().toISOString()}.csv`;
    }
    hiddenElement.click();

    setCsvDataReady(false);
    hiddenElement.remove();
    reset();
  };

  return (
    <Button
      className="z-btn-1"
      onClick={onCsvExportClick}
      type="button"
      hidden={!exportProps?.hasExport}
      disabled={loading}
    >
      {csvDataReady === true ? 'Download CSV ✅' : 'Prepare CSV'}
    </Button>
  );
};

export default CsvExport;
